import { css } from 'lit';
import bind from 'bind-decorator';
import { CSSResultArray, TemplateResult, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import HTTPMethod from 'http-method-enum';
import { APIConfig } from '@config/ConfigCARSx';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import { isValidString } from '@/utils/utils';
import store from '@/redux/redux-store';
import { NotificationErrorType } from '@typings/shared-types';
import { ConfigWZRT } from '@/config/ConfigWZRT';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import Login from '../login/login';
import { ConfigLoginApi } from '../../config/ConfigLogin';
import APIRequest, { getAPIHeaders } from '../../redux/APIRequest';
import { EMAIL_ADDRESS_REGEX } from '../../constants';
import { showMainBanner } from '../../redux/redux-ui';
import './work-zone-training-link';

const INVALID_EMAIL_ADDRESS_ERROR_MESSAGE = 'You must enter a valid email address.';

@customElement('work-zone-login')
export default class WorkZoneLogin extends Login {
	private emailInputRef: Ref<HTMLInputElement> = createRef();

	@state() private emailAddressError?: string;

	public static override get styles(): CSSResultArray {
		return [
			...super.styles,
			css`
				p.description {
					color: white;
					font-weight: bold;
					font-size: 1.5rem;
				}
				p.title {
					font-size: 2rem;
					text-align: center;
				}
				span.error {
					display: block;
					padding: var(--ggutter) var(--ggutter) calc(var(--ggutter) / 2) calc(var(--ggutter) * 1.5);
					background-color: rgb(var(--brand-alert));
					color: white;
				}
			`,
		];
	}

	titleTemplate() {
		return html`<li>
			<p class="description title">${unsafeHTML(ConfigWZRT.login.title)}</p>
		</li>`;
	}

	descriptionsAboveTemplate() {
		return ConfigWZRT.login.descriptionsAbove.map(
			(description) => html`<li>
				<p class="description">${unsafeHTML(description)}</p>
			</li>`,
		);
	}

	descriptionsBelowTemplate() {
		return ConfigWZRT.login.descriptionsBelow.map(
			(description) => html`<li>
				<p class="description">${unsafeHTML(description)}</p>
			</li>`,
		);
	}

	override loginFormTemplate(): TemplateResult {
		return html`<ol class="formElements">
			${this.titleTemplate()} ${this.descriptionsAboveTemplate()}

			<li>
				<input
					${ref(this.emailInputRef)}
					type="text"
					title="Email"
					id="login-email"
					class=${classMap({
						invalid: this.invalidLogin,
					})}
					name="login-email"
					required
					autofocus
					autocomplete="email"
					autocapitalize="none"
				/><label for="login-email">Email</label>
				${this.emailAddressError
					? html`<span class="error">${this.emailAddressError}</span>`
					: nothing}
			</li>
			<li>
				<button
					type="submit"
					name="login-submit"
					class="${classMap({
						bbuttonbase: true,
						cta: true,
						disabled: this.isWaitingForLoginResponse,
					})}"
				>
					Verify Email
				</button>
			</li>
			<li>
				<work-zone-training-link></work-zone-training-link>
			</li>

			${this.descriptionsBelowTemplate()}
		</ol>`;
	}

	@bind
	override async onSubmitForm(e: Event): Promise<void> {
		e.preventDefault();

		this.isWaitingForLoginResponse = true;

		const email = this.emailInputRef.value?.value;

		if (!isValidString(email) || !EMAIL_ADDRESS_REGEX.test(email)) {
			this.emailInputRef.value?.classList.add('validated');
			this.emailInputRef.value?.focus();
			this.emailInputRef.value?.select();
			this.invalidLogin = true;

			this.emailAddressError = INVALID_EMAIL_ADDRESS_ERROR_MESSAGE;

			this.isWaitingForLoginResponse = false;

			return;
		}

		this.emailAddressError = undefined;

		const apiRequestReturn = await APIRequest(
			new Request(
				new URL(
					ConfigLoginApi.validateEmail(encodeURIComponent(email)),
					APIConfig.endpointURLBase,
				).href,
				{
					method: HTTPMethod.GET,
					headers: new Headers({ ...getAPIHeaders() }),
				},
			),
		);

		if (apiRequestReturn.response?.ok) {
			store.dispatch(
				showMainBanner(NotificationErrorType.SUCCESS, { title: 'Verification email sent' }),
			);
		} else {
			store.dispatch(
				showMainBanner(NotificationErrorType.ERROR, { title: 'Error sending verification email' }),
			);
		}

		this.isWaitingForLoginResponse = false;
	}
}
